import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { type WebTrafficCompanySummaryDataType } from '@/api/activity';
import { Link, Text } from '@/components/typography';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/router';

import StatField from './StatField';
import StatFieldItem from './StatFieldItem';

type CompanyActivityListsProps = {
  data?: WebTrafficCompanySummaryDataType;
};

const CompanyActivityLists = ({ data }: CompanyActivityListsProps) => {
  if (!data) {
    return null;
  }

  const hasWebActivity = data.webActivityPages && data.webActivityPages.length > 0;
  const hasOpportunities = data.opportunities && data.opportunities.length > 0;
  const hasKeywords = data.keywords && data.keywords.length > 0;
  const hasIntents = data.intents && data.intents.length > 0;

  if (!hasWebActivity && !hasOpportunities && !hasKeywords && !hasIntents) {
    return null;
  }

  const sectionCount = [hasWebActivity, hasOpportunities, hasKeywords, hasIntents].filter(
    Boolean,
  ).length;

  return (
    <div className={cn('gap-x-4 px-4 pb-4', sectionCount >= 2 && 'columns-2')}>
      {hasWebActivity && (
        <div className="mb-4 break-inside-avoid px-2 last:mb-0">
          <StatField label={<Trans>Web Activity</Trans>}>
            {data.webActivityPages.map((path) => {
              const urlPath = path === '' ? '/' : path;
              return (
                <StatFieldItem key={urlPath} tooltip={urlPath}>
                  <Text variant="caption1">{urlPath}</Text>
                </StatFieldItem>
              );
            })}
          </StatField>
        </div>
      )}
      {hasOpportunities && (
        <div className="mb-4 break-inside-avoid px-2 last:mb-0">
          <StatField label={<Trans>Opportunities</Trans>}>
            {data.opportunities?.map((opportunity) => (
              <StatFieldItem
                key={opportunity.id}
                tooltip={opportunity.name}
                tooltipTitle={t`Opportunities`}
              >
                <Link variant="caption1" to={ROUTES.opportunityById(opportunity.id)}>
                  {opportunity.name}
                </Link>
              </StatFieldItem>
            ))}
          </StatField>
        </div>
      )}
      {hasKeywords && (
        <div className="mb-4 break-inside-avoid px-2 last:mb-0">
          <StatField label={<Trans>Keywords</Trans>}>
            {data.keywords.map((keyword) => (
              <StatFieldItem key={keyword.id} tooltip={keyword.name}>
                <Text variant="caption1">{keyword.name}</Text>
              </StatFieldItem>
            ))}
          </StatField>
        </div>
      )}
      {hasIntents && (
        <div className="mb-4 break-inside-avoid px-2 last:mb-0">
          <StatField label={<Trans>Intents</Trans>}>
            {data.intents.map((intent) => (
              <StatFieldItem key={intent.id} tooltip={intent.name}>
                <Text variant="caption1">
                  {intent.provider.name} {intent.type}: {intent.name}
                </Text>
              </StatFieldItem>
            ))}
          </StatField>
        </div>
      )}
    </div>
  );
};

export default CompanyActivityLists;
